import React from "react";
import "../../assets/stylesheets/gip-login-modal.css";
import * as icons from "../../assets/svg/login-icons";
import {
  validateEmail,
  validatePhone,
} from "../../services/ApiService";
import {
  firebasePasswordLogin,
  firebaseProviderLogin,
  getMicrosoftLogoutUrl,
} from "../../services/firebase";
import {
  PROVIDERS_TYPE,
  MESSAGES_TYPES,
  MOBILE_HOSTS,
  DEFAULT_HOSTS,
  URL_PARAMS,
  USER_TYPES,
  LOCAL_STORAGE_KEYS,
  SAML_PROVIDER_CLALIT,
  SAML_PROVIDER_LEUMIT
} from "../../assets/appConstants";
import LanguageSelector from "../onBoarding/components/language-selector";
import { supportedLanguages } from "../../assets/i18h/appTranslation";
import { translate } from "../../vendors/react-translate/ReactTranslate";
import {
  ClosedEyeIcon,
  OpenEyeIcon,
} from "../../assets/svg/password-modal-icons";
import OtpPrompt from "./OtpPromptModal";
import CloseButton from "../common/close-button";
import ToggleSwitch from "../common/ToggleSwitch";
import mixPanelService from "../../services/mixPanel/mixPanelService";
import SsoProviderLoginModal from "./SsoProviderLoginModal";
import SsoSwitchUserModal from "./SsoSwitchUserModal";
import SettingsButton from "./SettingsButton";
import {getUrlParam, postMessage} from "../../services/utils";
import HelpButton from "../onBoarding/components/help-button";
import RespondioChat from "../RespondioChat";

class LoginModal extends React.Component {
  constructor(props) {
    super(props);

    this.host = getUrlParam(URL_PARAMS.HOST, true);
    this.appType = getUrlParam(URL_PARAMS.APPTYPE, true);

    this.state = {
      isValid: true,
      emailErrMsg: "",
      passwordErrMsg: "",
      language: props.language,
      showPassword: false,
      inputType: "password",
      otpPrompt: this.isPatientMobile() ? true : false,
      showSsoModal: false,
      keyboardVisible: false,
      samlProviderCleared: false,
      showChat: false,
      switchUserLogoutUrl: null,
      isSubmitting: false,
    };

    this.originalHeight = window.innerHeight;
    this.handleResize = this.handleResize.bind(this);
    this.onInputFocus = this.onInputFocus.bind(this);
    this.toggleOtpPrompt = this.toggleOtpPrompt.bind(this);
    this.onInputBlur = this.onInputBlur.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.toggleChat = this.toggleChat.bind(this);
  }

  componentDidMount() {
    setTimeout(this.checkInputs, 200);
    setTimeout(this.checkInputs, 600);
    document.addEventListener("keydown", this.handleKeyPress);
    
    if (this.host === MOBILE_HOSTS.ANDROID) {
      window.addEventListener('resize', this.handleResize);
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyPress);
    
    if (this.host === MOBILE_HOSTS.ANDROID) {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  handleResize() {
    const newHeight = window.innerHeight;
    const keyboardVisible = newHeight < this.originalHeight;
    
    if (this.state.keyboardVisible !== keyboardVisible) {
      this.setState({ keyboardVisible });
    }
  }

  checkInputs() {
    const inputs = document.querySelectorAll(".gip-input");
    const autofilledInputs = document.querySelectorAll(
      "input:-webkit-autofill"
    );
    autofilledInputs.forEach((input) => {
      if (input.offsetParent) input.offsetParent.classList.add("hasValue");
    });
    inputs.forEach((input) => {
      if (input.value) input.classList.add("hasValue");
    });
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.open !== prevProps.open) {
      setTimeout(this.checkInputs, 200);
      setTimeout(this.checkInputs, 600);
    }
    if (this.props.language !== prevProps.language) {
      this.setState({
        language: this.props.language,
      });
    }
  }

  handleKeyPress(e) {
    if (
      e.key === "Enter" &&
      !this.state.otpPrompt &&
      !this.state.showSsoModal
    ) {
      this.submitLogin(e);
    }
  }

  togglePassword() {
    const { showPassword } = this.state;
    let _show = !showPassword;
    let _inputType = _show ? "text" : "password";
    this.setState({
      inputType: _inputType,
      showPassword: _show,
    });
    mixPanelService.trackPasswordToggle(!showPassword);
    let password = document.querySelector("#loginPassword");
    password.focus();
  }

  onInputFocus(e) {
    this.checkInputs();
    e.target.offsetParent.classList.add("hasValue");
    this.setState({
      isValid: true,
    });
    e.target.classList.remove("invalid");
    if (document.activeElement.tagName === "INPUT") {
      if (typeof document.activeElement.scrollIntoViewIfNeeded === "function") {
        window.setTimeout(() => {
          document.activeElement.scrollIntoViewIfNeeded();
        }, 0);
      } else {
        window.setTimeout(() => {
          document.activeElement.scrollIntoView(false);
        }, 0);
      }
    }
  }

  toggleOtpPrompt() {
    this.setState((prevState) => ({
      otpPrompt: !prevState.otpPrompt,
    }));
  }

  onInputBlur(e) {
    this.checkInputs();
    if (e.target.value) return;
    e.target.offsetParent.classList.remove("hasValue");
  }

  submitLogin(e) {
    const { t } = this.props;
    e.preventDefault();

    if (this.state.isSubmitting) {
      return;
    }
    let identifier = document.querySelector("#loginEmail");
    let password = document.querySelector("#loginPassword");
    const identifierValue = identifier.value.trim();
    let emailErrMsg,
      passwordErrMsg,
      isValid = true;
    if (!identifierValue) {
      emailErrMsg = t("login.email.error");
      isValid = false;
    }
    if (!password.value) {
      passwordErrMsg = t("login.password.error");
      isValid = false;
    }
    if (!validateEmail(identifierValue) && !validatePhone(identifierValue)) {
      emailErrMsg = t("login.email.invalid");
      isValid = false;
    }

    if (password.value && password.value.length < 8) {
      passwordErrMsg = t("login.password.invalid");
      isValid = false;
    }

    if (!isValid) {
      this.setState({
        emailErrMsg,
        passwordErrMsg,
        isValid,
      });
    } else {
      this.setState(
        {
          isValid,
          isSubmitting: true,
        },
        () => {
          firebasePasswordLogin(identifierValue, password.value).finally(() => {
            this.setState({ isSubmitting: false });
          });
        }
      );
    }
  }

  switchLanguage(lang) {
    this.setState({
      language: lang,
    });
    this.props.setTranslation(lang);
  }

  /**
   * According to WC-7371 / Login App / Special behavior for iOS with SAML
   * @param {string} samlType Values from appConstants::PROVIDERS_TYPE
   * @returns {boolean}
   */
  shouldWeProceedWithSaml(samlType){
    if (getUrlParam(URL_PARAMS.HOST, true) === MOBILE_HOSTS.IOS && (samlType === PROVIDERS_TYPE.GOOGLE || samlType === PROVIDERS_TYPE.APPLE || samlType === PROVIDERS_TYPE.SAML)) {
      let messageType;
      if (samlType === PROVIDERS_TYPE.GOOGLE) {
        messageType = MESSAGES_TYPES.NATIVE_AUTH_WITH_GOOGLE;
      } else if (samlType === PROVIDERS_TYPE.APPLE) {
        messageType = MESSAGES_TYPES.NATIVE_AUTH_WITH_APPLE;
      } else {
        messageType = MESSAGES_TYPES.NATIVE_AUTH_WITH_SAML;
      }
      postMessage(messageType);
      return false;
    }
    return true;
  }

  shouldShowGoogleSignIn() {
    // Web apps: Show Google for both Clinician and Patient
    if (!this.host || (this.host !== MOBILE_HOSTS.IOS && this.host !== MOBILE_HOSTS.ANDROID)) {
      return true;
    }

    // iOS - Hide Google for patients (when appType is not clinician or not present)
    if (this.host === MOBILE_HOSTS.IOS && (!this.appType || this.appType !== USER_TYPES.CLINICIAN)) {
      return false;
    }

    // iOS - Patient app: Hide Google (including when appType is not provided)
    if (this.host === MOBILE_HOSTS.IOS && (this.appType === USER_TYPES.PATIENT || !this.appType)) {
      return false;
    }

    // Android - Both Clinician and Patient: Show Google
    return true;
  }

  shouldShowAppleSignIn() {
    // Do not show Apple Sign In for now
    return false;
  }

  shouldShowSSOSignIn() {
    // Web apps: Show SSO for both Clinician and Patient
    if (!this.host || (this.host !== MOBILE_HOSTS.IOS && this.host !== MOBILE_HOSTS.ANDROID)) {

      return true;
    }

    // iOS - Clinician app: Hide SSO
    if (this.host === MOBILE_HOSTS.IOS && this.appType === USER_TYPES.CLINICIAN) {
      return false;
    }

    // iOS - Patient app: Hide SSO
    if (this.host === MOBILE_HOSTS.IOS && this.appType !== USER_TYPES.CLINICIAN) {
      return false;
    }

    // Android - Clinician app: Show SSO
    if (this.host === MOBILE_HOSTS.ANDROID && this.appType === USER_TYPES.CLINICIAN) {
      return true;
    }

    // Android - Patient app: Hide SSO
    if (this.host === MOBILE_HOSTS.ANDROID && this.appType !== USER_TYPES.CLINICIAN) {
      return false;
    }

    return true;
  }

  shouldUsePreviousSamlProvider() {
    return localStorage.getItem(LOCAL_STORAGE_KEYS.LAST_SAML_PROVIDER) !== null;
  }

  getSamlProviderIcon() {
    const lastProvider = localStorage.getItem(LOCAL_STORAGE_KEYS.LAST_SAML_PROVIDER);
    switch(lastProvider) {
      case SAML_PROVIDER_CLALIT:
        return icons.ClalitProviderIcon();
      case SAML_PROVIDER_LEUMIT:
        return icons.LeumitProviderIcon();
      default:
        return icons.SsoIcon();
    }
  }

  handleSamlProviderCleared = () => {
    this.setState({ 
      samlProviderCleared: true 
    }, () => {
      this.setState({ samlProviderCleared: false });
    });
  };

  openSwitchUser = (e) => {
    e.stopPropagation();
    this.setState({ switchUserLogoutUrl: getMicrosoftLogoutUrl() });
  };

  closeSwitchUserModal = () => {
    this.setState({ switchUserLogoutUrl: null });
  };

  renderSsoLoginButton() {
    const usePreviousSamlProvider = this.shouldUsePreviousSamlProvider();
    const showSSO = this.shouldShowSSOSignIn();
    if (!showSSO) {
      return null;
    }

    const ssoInner = usePreviousSamlProvider ? (
      <>
        <div className="icon provider">{this.getSamlProviderIcon()}</div>
        Sign-in
      </>
    ) : (
      <>
        <div className="icon sso">{icons.SsoIcon()}</div>
        <div className="option-text" style={{ marginLeft: "20px" }}>
          Single Sign-On
        </div>
      </>
    );

    const onSsoClick = () => {
      if (this.shouldWeProceedWithSaml(PROVIDERS_TYPE.SAML)) {
        if (usePreviousSamlProvider) {
          firebaseProviderLogin(PROVIDERS_TYPE.SAML);
        } else {
          this.setState({ showSsoModal: true });
        }
      }
    };

    const ssoButton = (
      <div
        className="option option-full"
        style={usePreviousSamlProvider ? { flex: 1, minWidth: 0 } : undefined}
        onClick={onSsoClick}
      >
        {ssoInner}
      </div>
    );

    if (!usePreviousSamlProvider) {
      return ssoButton;
    }

    return (
      <div
        style={{
          display: "flex",
          width: "100%",
          gap: "10px",
          alignItems: "stretch",
        }}
      >
        {ssoButton}
        <button
          type="button"
          title="Switch user"
          aria-label="Switch user"
          onClick={this.openSwitchUser}
          style={{
            flex: "0 0 auto",
            width: 48,
            height: 48,
            alignSelf: "center",
            borderRadius: "50%",
            border: "1px solid #8b8b8b",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 0,
            margin: "10px 0",
          }}
        >
          <icons.LogoutIcon width={20} height={20} style={{ display: "block" }} />
        </button>
      </div>
    );
  }

  isPatientMobile() {
    const isPatientAppType = this.appType === USER_TYPES.PATIENT;
    const isPatientHost =
      this.host === MOBILE_HOSTS.ANDROID ||
      this.host === MOBILE_HOSTS.IOS ||
      this.host === DEFAULT_HOSTS.WEBSITE;

    if (isPatientAppType && isPatientHost) {
      return true;
    }

    return (this.host === MOBILE_HOSTS.ANDROID || this.host === MOBILE_HOSTS.IOS) && !this.appType;
  }


  toggleChat() {
    this.setState((prevState) => ({
      showChat: !prevState.showChat
    }));
  }

  renderGoogleButton(className = "option") {
    if (!this.shouldShowGoogleSignIn()) return null;
    
    return (
      <div
        className={className}
        onClick={() => {
          if (this.shouldWeProceedWithSaml(PROVIDERS_TYPE.GOOGLE)) {
            firebaseProviderLogin(PROVIDERS_TYPE.GOOGLE);
          }
        }}
      >
        <div className="icon google">{icons.GoogleIcon()}</div>
        Google
      </div>
    );
  }

  render() {
    const { open, t, countryCode } = this.props;
    const {
      emailErrMsg,
      passwordErrMsg,
      isValid,
      language,
      inputType,
      showPassword,
      otpPrompt,
      showSsoModal,
      keyboardVisible,
      showChat,
      switchUserLogoutUrl,
    } = this.state;
    const isHebrew = language === supportedLanguages.he;
    
    const platformClass = this.host === MOBILE_HOSTS.ANDROID ? 'android' : 
                          this.host === MOBILE_HOSTS.IOS ? 'ios' : '';

    const isAndroidKeyboardVisible = this.host === MOBILE_HOSTS.ANDROID && keyboardVisible;
    
    const footerStyle = isAndroidKeyboardVisible ? { display: 'none' } : {};
    const wizeCareLogoStyle = isAndroidKeyboardVisible ? { display: 'none' } : {};

    const showGoogle = this.shouldShowGoogleSignIn();
    const showApple = this.shouldShowAppleSignIn();
    const showSSO = this.shouldShowSSOSignIn();
    const isPatientMobile = this.isPatientMobile();
    const isClinicianOtpScreen = otpPrompt && !isPatientMobile;
    const isWebLogin =
      this.host !== MOBILE_HOSTS.IOS && this.host !== MOBILE_HOSTS.ANDROID;

    return (
      open && (
        <div className={`gip-modal-wrapper ${platformClass} ${isHebrew ? "heb" : ""}`}>
          {showChat && (
            <div className="chat-overlay">
              <RespondioChat
                language={language}
                t={t}
                onClose={this.toggleChat}
              />
            </div>
          )}
          <div className="gip-modal-content">

          <div style={{ position: "absolute", right: "12px", top: "8px", zIndex: 13000 }}>
            <SettingsButton
              t={t}
              language={language}
              onClearSamlProvider={this.handleSamlProviderCleared}
              showClinicianWebLoginSelector={isWebLogin && isPatientMobile}
            />
            </div>
            
          <div style={{ position: "absolute", left: "12px", top: "10px", zIndex: 13000 }}>
            <LanguageSelector
              language={language}
              switchLang={this.switchLanguage.bind(this)}
              isModal={true}
            />
          </div>
          
          <CloseButton />
          <div className="login-modal-header">
            <div className="title">{t("login.title")}</div>
            {otpPrompt  && (
              <div className="otp-secondary-title">
                {isClinicianOtpScreen
                  ? t("login.otp.clinician.secondary.title")
                  : t("login.otp.main.cta")}
              </div>
            )}
          </div>
          <div className={`form-wrapper ${isHebrew ? "heb" : ""} lang-${language}`}>
            {!showSsoModal && (
              <div className="input-wrapper">
                <ToggleSwitch
                  isPatientMobile={isPatientMobile}
                  toggleOtpPrompt={this.toggleOtpPrompt}
                  otpPrompt={otpPrompt}
                  t={t}
                  language={language}
                />
              </div>
            )}

            {otpPrompt && (
              <OtpPrompt
                countryCode={countryCode}
                language={language}
                isPatientMobile={isPatientMobile}
                isWebLogin={isWebLogin}
                onClose={() => this.setState({ otpPrompt: false })}
              />
            )}
            {showSsoModal && (
              <SsoProviderLoginModal
                onClose={() => this.setState({ showSsoModal: false })}
              />
            )}
            <SsoSwitchUserModal
              logoutUrl={switchUserLogoutUrl}
              onClose={this.closeSwitchUserModal}
              onAfterSsoCleared={() => {
                this.setState({ switchUserLogoutUrl: null });
                this.handleSamlProviderCleared();
              }}
              t={t}
              language={language}
            />

            <div className="input-wrapper">
              <div className="input-label">{t("login.email")}</div>
              <div className="input-floating-label">{t("login.email")}</div>
              <input
                type="email"
                id={"loginEmail"}
                onFocus={this.onInputFocus.bind(this)}
                onBlur={this.onInputBlur.bind(this)}
                onChange={this.checkInputs}
                className={`gip-input ${!isValid ? "invalid" : ""}`}
              />
              {!isValid && (
                <div className={"error-message"}>{emailErrMsg}</div>
              )}
            </div>
            <div className="input-wrapper">
              <div className="input-label">{t("login.password")}</div>
              <div className="input-floating-label">
                {t("login.password")}
              </div>
              <input
                type={inputType}
                id={"loginPassword"}
                onFocus={this.onInputFocus.bind(this)}
                onBlur={this.onInputBlur.bind(this)}
                onChange={this.checkInputs}
                className={`gip-input ${!isValid ? "invalid" : ""}`}
              />
              <div
                className="eye-icon"
                onClick={this.togglePassword.bind(this)}
              >
                {showPassword ? <OpenEyeIcon /> : <ClosedEyeIcon />}
              </div>
              {!isValid && (
                <div className={"error-message"}>{passwordErrMsg}</div>
              )}
            </div>
          </div>
          <div className="btn-wrapper">
            <div className="action-btn" onClick={this.submitLogin.bind(this)}>
              {t("login.btn")}
            </div>
          </div>
          <div
            className="forgot-password"
            onClick={() => {
              this.props.openPasswordModal();
              this.props.handleClose();
            }}
          >
            {t("login.password.btn")}
          </div>
          {(showGoogle || showApple || showSSO) && (
          <div className="hr-title">{t("login.separator")}</div>
        )}
          <div className="options-wrapper">
            {/* Apple and Google buttons row - only for iOS patients */}
            {showApple && (
              <div className="options-row">
                {this.renderGoogleButton("option option-half")}
                <div
                  className="option option-half"
                  onClick={() => {
                    if (this.shouldWeProceedWithSaml(PROVIDERS_TYPE.APPLE)) {
                      firebaseProviderLogin(PROVIDERS_TYPE.APPLE);
                      
                    }
                  }}
                >
                  <div className="icon apple">{icons.AppleIcon()}</div>
                  Apple
                </div>
              </div>
            )}
            
            {/* SSO button - independent of Apple, shows for both iOS and non-iOS */}
            {this.renderSsoLoginButton()}
            
            {/* Google button - for non-iOS patients (when Apple is not showing) */}
            {!showApple && this.renderGoogleButton()}
          </div>
          
        </div>
        {otpPrompt && (
          <div className="wize-care-logo-otp" style={wizeCareLogoStyle}>
            <icons.WizeCareLogoOtp />
          </div>
        )}
        <div className="footer-area" style={footerStyle}>
          <div className="footer-whatsapp-helper">
            <HelpButton t={t} language={language} onChatOpen={this.toggleChat}/>
          </div>
        </div>
      </div>
      )
    );
  }
}

export default translate("login")(LoginModal);